import { isJoker } from '../card';
import { RANK_ORDER_STANDARD, STANDARD_SUITS } from '../constants';
import { Rank } from '../enums';
import { Card, Entry } from '../types';
import { evaluateFrontHand } from './front';
import { evaluateHighHand } from './high';

const PLAYABLE_RANKS = RANK_ORDER_STANDARD.filter((r) => r !== Rank.JOKER);

function generateSubstitutions(cards: Card[], jokerIndices: number[]): Card[][] {
  if (jokerIndices.length === 0) {
    return [cards];
  }

  const [firstIdx, ...restIndices] = jokerIndices;
  const result: Card[][] = [];

  for (const rank of PLAYABLE_RANKS) {
    for (const suit of STANDARD_SUITS) {
      const newCards = [...cards];
      newCards[firstIdx] = { rank, suit };

      if (restIndices.length === 0) {
        result.push(newCards);
      } else {
        const subResults = generateSubstitutions(newCards, restIndices);
        result.push(...subResults);
      }
    }
  }

  return result;
}

function findBestHand(cards: readonly Card[], evaluator: (cards: readonly Card[]) => Entry): Entry {
  const jokerIndices: number[] = [];
  for (let i = 0; i < cards.length; i++) {
    if (isJoker(cards[i])) {
      jokerIndices.push(i);
    }
  }

  if (jokerIndices.length === 0) {
    return evaluator(cards);
  }

  const allHands = generateSubstitutions([...cards], jokerIndices);

  let bestEntry: Entry | null = null;
  for (const hand of allHands) {
    try {
      const entry = evaluator(hand);
      if (!bestEntry || entry.index > bestEntry.index) {
        bestEntry = entry;
      }
    } catch {
      continue;
    }
  }

  if (!bestEntry) {
    throw new Error('Could not find valid hand with joker substitution');
  }
  return bestEntry;
}

export function evaluateWithJoker(cards: readonly Card[]): Entry {
  if (cards.length !== 5) {
    throw new Error(`Joker hand requires exactly 5 cards, got ${cards.length}`);
  }
  return findBestHand(cards, evaluateHighHand);
}

export function evaluateFrontWithJoker(cards: readonly Card[]): Entry {
  if (cards.length !== 3) {
    throw new Error(`Joker front hand requires exactly 3 cards, got ${cards.length}`);
  }
  return findBestHand(cards, evaluateFrontHand);
}
